'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'

import { Label } from '../../components'
import { Typography, type TypographyProps } from '../typography'

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
  extra?: React.ReactNode
  displayValidation?: boolean
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  extra,
  displayValidation = true,
  ...props
}: ControllerProps<TFieldValues, TName> & {
  extra?: React.ReactNode
  displayValidation?: boolean
}) => {
  const value = React.useMemo(
    () => ({ name: props.name, extra, displayValidation }),
    [props.name, extra, displayValidation],
  )

  return (
    <FormFieldContext.Provider value={value}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    extra: fieldContext.extra,
    displayValidation: fieldContext.displayValidation,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

function FormItem({ ...props }: React.ComponentProps<'div'>) {
  const id = React.useId()
  const value = React.useMemo(() => ({ id }), [id])

  return (
    <FormItemContext.Provider value={value}>
      <div data-slot='form-item' {...props} />
    </FormItemContext.Provider>
  )
}

function FormLabel({ ...props }: TypographyProps) {
  const { error, formItemId, extra } = useFormField()

  return (
    <div className='flex justify-between items-center'>
      <Label
        data-slot='form-label'
        data-error={!!error}
        htmlFor={formItemId}
        {...props}
      />
      {extra}
    </div>
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      data-slot='form-control'
      data-error={!!error}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormDescription({ ...props }: TypographyProps) {
  const { formDescriptionId, error } = useFormField()
  if (error) {
    return null
  }
  return (
    <Typography
      variant='p'
      data-slot='form-description'
      id={formDescriptionId}
      {...props}
    />
  )
}

function FormMessage({
  haveCaption,
  ...props
}: TypographyProps & { haveCaption?: boolean }) {
  const { error, formMessageId, displayValidation } = useFormField()
  const body = error ? String(error?.message ?? '') : props.children

  if (((haveCaption && !error) || !displayValidation) && !props.children) {
    return null
  }

  // Always render, but hide if no message
  const isVisible = !!body

  return (
    <Typography
      variant='p'
      data-slot='form-message'
      id={formMessageId}
      className={isVisible ? '' : ' invisible'}
      aria-hidden={!isVisible}
      {...props}
    >
      {body || '\u00A0'}
    </Typography>
  )
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
}
